import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Info } from 'lucide-react';
import styles from './Carousel.module.css';

interface CarouselItem {
    url: string;
    caption: string;
}

interface CarouselProps {
    items: CarouselItem[];
}

const Carousel: React.FC<CarouselProps> = ({ items }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [direction, setDirection] = useState(0); // -1 for left, 1 for right

    const slideNext = useCallback(() => {
        setDirection(1);
        setCurrentIndex((prev) => (prev + 1) % items.length);
    }, [items.length]);

    const slidePrev = useCallback(() => {
        setDirection(-1);
        setCurrentIndex((prev) => (prev - 1 + items.length) % items.length);
    }, [items.length]);

    // Auto-play (optional, but premium)
    useEffect(() => {
        const timer = setInterval(() => {
            slideNext();
        }, 8000); // 8 seconds per slide
        return () => clearInterval(timer);
    }, [slideNext]);

    if (!items || items.length === 0) return null;

    const variants = {
        enter: (direction: number) => ({
            x: direction > 0 ? 1000 : -1000,
            opacity: 0,
            scale: 0.95
        }),
        center: {
            zIndex: 1,
            x: 0,
            opacity: 1,
            scale: 1
        },
        exit: (direction: number) => ({
            zIndex: 0,
            x: direction < 0 ? 1000 : -1000,
            opacity: 0,
            scale: 0.95
        })
    };

    return (
        <div className={styles.carouselContainer}>
            <div className={styles.slidesWrapper}>
                <AnimatePresence initial={false} custom={direction}>
                    <motion.div
                        key={currentIndex}
                        custom={direction}
                        variants={variants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={{
                            x: { type: "spring", stiffness: 300, damping: 30 },
                            opacity: { duration: 0.5 },
                            scale: { duration: 0.5 }
                        }}
                        className={styles.slide}
                    >
                        <img 
                            src={items[currentIndex].url} 
                            alt={items[currentIndex].caption || `Slide ${currentIndex + 1}`}
                            className={styles.slideImage}
                        />
                        {items[currentIndex].caption && (
                            <div className={styles.captionOverlay}>
                                <div className={styles.captionBar}>
                                    <Info size={14} className={styles.infoIcon} />
                                    <span>{items[currentIndex].caption}</span>
                                </div>
                            </div>
                        )}
                    </motion.div>
                </AnimatePresence>

                {items.length > 1 && (
                    <>
                        <button 
                            className={`${styles.navButton} ${styles.prev}`} 
                            onClick={(e) => { e.stopPropagation(); slidePrev(); }}
                            aria-label="Previous slide"
                        >
                            <ChevronLeft size={24} />
                        </button>
                        <button 
                            className={`${styles.navButton} ${styles.next}`} 
                            onClick={(e) => { e.stopPropagation(); slideNext(); }}
                            aria-label="Next slide"
                        >
                            <ChevronRight size={24} />
                        </button>
                        
                        <div className={styles.indicators}>
                            {items.map((_, index) => (
                                <button
                                    key={index}
                                    className={`${styles.indicator} ${index === currentIndex ? styles.active : ''}`}
                                    onClick={() => {
                                        setDirection(index > currentIndex ? 1 : -1);
                                        setCurrentIndex(index);
                                    }}
                                    aria-label={`Go to slide ${index + 1}`}
                                />
                            ))}
                        </div>
                    </>
                )}
            </div>
            
            <div className={styles.counter}>
                {currentIndex + 1} / {items.length}
            </div>
        </div>
    );
};

export default Carousel;
